import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button, LinearProgress } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { PipelinesPaths } from 'app/configs/routesConfig/pipelines';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { shouldResetCountyField } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { ConfirmationModalType } from 'constants/confirmationDialog';
import { CONFLICT_TYPE } from 'constants/http';
import { LoanApplicationTabRouteParam, PurposeOfChattel } from 'constants/loanApplication';
import { SYSTEM_PROPERTIES } from 'constants/properties';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import usePrevious from 'hooks/usePrevious';
import { getDefaultErrorMessage } from 'utils/axios';
import { getConditionalObject, normalizeRequestPhoneNumber } from 'utils/data';
import { checkIsSpecificConflictError } from 'utils/error';
import { getActionBody, transformNameInModelIntoFormName } from 'utils/formGenerator';
import {
  checkIsChattelLoanPurpose,
  checkIsPurchaseFlow,
  checkIsRefinanceFlow,
} from 'utils/loanApplication';
import { getChangedFieldsByConfig, getConfigInfo } from 'utils/loanManagement';
import { logger } from 'utils/logger';

import { selectIsClaimAllowed, selectIsSubCompanyUser } from 'store/auth/selectors';
import {
  selectCompanyLeadSourcesOptions,
  selectCompanyPropertyByPropertyName,
} from 'store/companies/selectors';
import {
  closeDialogAction,
  openConfirmationDialogAction,
  showErrorMessageAction,
  showMessageAction,
} from 'store/global/actions';
import {
  createLeadAction,
  editLeadByIdAction,
  getLoanApplicationHeaderViewAction,
} from 'store/loanApplications/actions';
import { convertLeadToLoanAction } from 'store/loanApplications/actions/lead.actions';
import {
  selectCommonInfoLoanApplication,
  selectFixedLeadFromApplication,
  selectMappedLeadChangedProps,
  selectSortedCountiesByStates,
} from 'store/loanApplications/selectors';
import { getSubCompaniesAction } from 'store/subCompanies/actions';
import { selectAvailableSubCompaniesOptions } from 'store/subCompanies/selectors';

import { LeadTestIds } from '../../Lead.testIds';

import {
  getAppraisedValue,
  getClearedNumberFields,
  getConvertAfterCreateErrorMessage,
  getDisplayedNumberValue,
  getSyncNumberValue,
  resolveLeadDownPaymentSync,
} from './LeadInfo.utils';
import {
  getLeadEditConfig,
  leadFormFields,
  LOCKED_STREET_ADDRESS_VALUE,
} from './LeadInfoEdit.config';

// loanDownPaymentPercent is a UI-only derived field (see the down-payment sync effect below) --
// getActionBody pulls it from leadFormFields like every other field, so every submit payload must
// explicitly drop it here rather than sending it to the server alongside loanDownPayment.
// chattelDetails (LEN-133) is only meaningful to the convert endpoint for a chattel purpose -- see
// getChattelDetailsValues -- so it is dropped from the shared payload.
const EXCLUDED_SUBMIT_FIELDS = [
  'property',
  'chattelDetails',
  leadFormFields.loanDownPaymentPercent.nameInModel,
];

// Nested chattelDetails.* fields are flattened in form state (transformNameInModelIntoFormName).
const PURPOSE_OF_CHATTEL_FORM_NAME = transformNameInModelIntoFormName(
  leadFormFields.purposeOfChattel.nameInModel,
);
const CHATTEL_SALES_PRICE_FORM_NAME = transformNameInModelIntoFormName(
  leadFormFields.chattelSalesPrice.nameInModel,
);

// The lead slice holds whichever lead was last loaded for a conversion, so a brand-new lead must
// not seed its form from it.
const NEW_LEAD_DATA = {};

const getChattelDetailsValues = formValues =>
  getConditionalObject(checkIsChattelLoanPurpose(formValues.loanPurpose), {
    chattelDetails: formValues.chattelDetails,
  });

const getPropertyValues = formValues =>
  Object.entries(formValues.property).reduce((acc, [name, value]) => {
    const fieldName = `property${_.upperFirst(name)}`;
    acc[fieldName] = value;

    return acc;
  }, {});

// FH-189: lead creation auto-assigns the creator as Loan Officer, but skips silently when their license
// does not cover the subject property's state. Only that skip reason is actionable for the creator. The
// copy names the CREATOR's placement, not the role: the assignment engine often staffs a licensed user
// moments later, so "Loan Officer not assigned" would stop being true.
const LOAN_OFFICER_NOT_LICENSED_SKIP_REASON = 'NotLicensedInState';

const warnIfLoanOfficerNotAssigned = (dispatch, loanOfficer) => {
  if (loanOfficer?.skipReason !== LOAN_OFFICER_NOT_LICENSED_SKIP_REASON) {
    return;
  }

  dispatch(
    showMessageAction({
      message: `You were not assigned as Loan Officer: you are not licensed in ${loanOfficer.stateCode}.`,
      variant: 'warning',
    }),
  );
};

const LeadInformationForm = ({
  isLoading,
  setIsLoading,
  onCancelEditForm,
  isCreateNewLead,
  isConvertLeadToLoan,
  createAsLoan = false,
  formId = null,
  hasExternalSubmit = false,
  onSubmit: onCustomSubmit,
  onLoadingChange,
  onDirtyChange,
}) => {
  const dispatch = useDispatch();

  const { push } = useHistory();

  const pendingChangesData = useSelector(selectMappedLeadChangedProps);
  const counties = useSelector(selectSortedCountiesByStates);
  const storedLeadData = useSelector(selectFixedLeadFromApplication);
  const leadSourcesOptions = useSelector(selectCompanyLeadSourcesOptions);
  const { loanType } = useSelector(selectCommonInfoLoanApplication) || {};
  const subCompaniesOptions = useSelector(selectAvailableSubCompaniesOptions());
  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
  const isSelectSubCompanyAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.MANUAL_LEAD_CREATION_SELECT_SUB_COMPANY),
  );
  const isCreateTestLoanAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CREATE_TEST_LOAN),
  );
  const companyId = useCompanyId();
  const leadCreationFieldsDisabledProperty = useSelector(
    selectCompanyPropertyByPropertyName(companyId, SYSTEM_PROPERTIES.LEAD_CREATION_FIELDS_DISABLED),
  );
  const isLeadCreationFieldsDisabled = leadCreationFieldsDisabledProperty?.propertyValue === 'true';

  const isNewLead = isCreateNewLead && !isConvertLeadToLoan;
  const isSubCompanyFieldShown = !isSubCompanyUser && isSelectSubCompanyAllowed && isNewLead;
  const leadData = isNewLead ? NEW_LEAD_DATA : storedLeadData;
  const { loanPurpose, refinancePurpose, propertyState, helocPurpose } = leadData;
  const leadPurposeOfChattel = _.get(leadData, leadFormFields.purposeOfChattel.nameInModel);

  const config = useMemo(
    () =>
      getLeadEditConfig({
        loanPurpose,
        loanType,
        refinancePurpose,
        counties,
        propertyState,
        leadSourcesOptions,
        isConvertLeadToLoan: isConvertLeadToLoan || createAsLoan,
        helocPurpose,
        isSubCompanyFieldShown,
        isCreateTestLoanAllowed,
        isLeadCreationFieldsDisabled,
        purposeOfChattel: leadPurposeOfChattel,
      }),
    [
      loanPurpose,
      loanType,
      refinancePurpose,
      counties,
      propertyState,
      leadSourcesOptions,
      isConvertLeadToLoan,
      createAsLoan,
      helocPurpose,
      isSubCompanyFieldShown,
      isCreateTestLoanAllowed,
      isLeadCreationFieldsDisabled,
      leadPurposeOfChattel,
    ],
  );

  const { initialFormValues, fieldsConfig, initialConfig } = useMemo(() => {
    return getConfigInfo({
      applicationData: {
        ...leadData,
        ...getConditionalObject(isSubCompanyFieldShown, {
          subCompanyId: '',
        }),
      },
      config,
    });
  }, [config, leadData, isSubCompanyFieldShown]);

  const { changedFields } = useMemo(
    () => getChangedFieldsByConfig({ pendingChangesData, initialConfig }),
    [pendingChangesData, initialConfig],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    setValidationErrors,
    validateField,
    validateForm,
    isFormChanged,
  } = useForm(initialFormValues, fieldsConfig);

  const hasPropertyStreet = !!form.propertystreet;
  const formPurposeOfChattel = form[PURPOSE_OF_CHATTEL_FORM_NAME]?.value;
  const chattelSalesPrice = form[CHATTEL_SALES_PRICE_FORM_NAME];
  const isChattelPurchase =
    checkIsChattelLoanPurpose(form.loanPurpose?.value) &&
    formPurposeOfChattel === PurposeOfChattel.PURCHASE;

  const formGeneratorConfig = useMemo(() => {
    return getLeadEditConfig({
      counties,
      propertyState: form.propertystate?.value,
      loanPurpose: form.loanPurpose?.value,
      loanType,
      refinancePurpose: form.refinancePurpose?.value,
      leadSourcesOptions,
      isCreateNewLead,
      isConvertLeadToLoan: isConvertLeadToLoan || createAsLoan,
      helocPurpose: form.helocPurpose?.value,
      isSubCompanyFieldShown,
      isCreateTestLoanAllowed,
      subCompaniesOptions,
      isLeadCreationFieldsDisabled,
      hasPropertyStreet,
      purposeOfChattel: formPurposeOfChattel,
    });
  }, [
    counties,
    hasPropertyStreet,
    formPurposeOfChattel,
    form.propertystate?.value,
    form.loanPurpose?.value,
    form.refinancePurpose?.value,
    form.helocPurpose?.value,
    loanType,
    leadSourcesOptions,
    isCreateNewLead,
    isConvertLeadToLoan,
    createAsLoan,
    isSubCompanyFieldShown,
    isCreateTestLoanAllowed,
    subCompaniesOptions,
    isLeadCreationFieldsDisabled,
  ]);

  useEffect(() => {
    const { schema } = getConfigInfo({
      applicationData: leadData,
      config: formGeneratorConfig,
    });
    setSchema(schema);
  }, [formGeneratorConfig, leadData, setSchema, leadSourcesOptions]);

  const prevLoanPurposeValue = usePrevious(form.loanPurpose?.value);
  const isLoanPurposeChanged =
    !!prevLoanPurposeValue && prevLoanPurposeValue !== form.loanPurpose?.value;

  useEffect(() => {
    if (!isLoanPurposeChanged) {
      return;
    }

    setMultipleInForm(
      {
        ...getClearedNumberFields([
          leadFormFields.loanAmount.nameInModel,
          leadFormFields.propertyValue.nameInModel,
          leadFormFields.loanCashOutAmount.nameInModel,
          leadFormFields.loanDownPayment.nameInModel,
          leadFormFields.loanDownPaymentPercent.nameInModel,
          CHATTEL_SALES_PRICE_FORM_NAME,
        ]),
        [leadFormFields.refinancePurpose.nameInModel]: undefined,
      },
      false,
    );
    setValidationErrors(prevErrors => ({
      ..._.cloneDeep(prevErrors),
      [leadFormFields.loanDownPayment.nameInModel]: '',
      [leadFormFields.loanCashOutAmount.nameInModel]: '',
    }));
  }, [isLoanPurposeChanged, setMultipleInForm, setValidationErrors]);

  // LEN-199: Sales Price and Down Payment are only shown for a chattel purchase -- moving off it
  // must not leave hidden values in the convert payload.
  const prevIsChattelPurchase = usePrevious(isChattelPurchase);
  const isChattelPurchaseLeft = !!prevIsChattelPurchase && !isChattelPurchase;

  useEffect(() => {
    if (!isChattelPurchaseLeft) {
      return;
    }

    setMultipleInForm(
      getClearedNumberFields([
        leadFormFields.loanDownPayment.nameInModel,
        leadFormFields.loanDownPaymentPercent.nameInModel,
        CHATTEL_SALES_PRICE_FORM_NAME,
      ]),
      false,
    );
    setValidationErrors(prevErrors => ({
      ..._.cloneDeep(prevErrors),
      [leadFormFields.loanDownPayment.nameInModel]: '',
    }));
  }, [isChattelPurchaseLeft, setMultipleInForm, setValidationErrors]);

  useEffect(() => {
    const shouldResetCounty = shouldResetCountyField({
      counties,
      formState: form.propertystate?.value,
      formCounty: form.propertycounty?.value,
    });

    if (shouldResetCounty) {
      setInForm('propertycounty', null, false);
    }
  }, [counties, form.propertycounty?.value, form.propertystate?.value, setInForm]);

  useEffect(() => {
    if (!isLeadCreationFieldsDisabled) {
      return;
    }

    const isPurchase = checkIsPurchaseFlow(form.loanPurpose?.value);
    // Street (FieldType.ADDRESS/INPUT) stores its form value as a raw string, not a { value } object.
    const streetValue = form.propertystreet;

    if (isPurchase && streetValue !== LOCKED_STREET_ADDRESS_VALUE) {
      setInForm('propertystreet', LOCKED_STREET_ADDRESS_VALUE, false);

      return;
    }

    if (!isPurchase && streetValue === LOCKED_STREET_ADDRESS_VALUE) {
      setInForm('propertystreet', '', false);
    }
  }, [isLeadCreationFieldsDisabled, form.loanPurpose?.value, form.propertystreet, setInForm]);

  // Keeps loanDownPayment ($) <-> loanDownPaymentPercent (%, UI-only) consistent with the
  // Purchase Price (form.propertyValue). No Loan Amount involvement here -- unlike the Loan Info
  // editor's down payment pair, a lead/loan-creation form has no baseLoanAmount invariant to hold.
  const downPaymentSyncRef = useRef(null);

  useEffect(() => {
    const isPurchase = checkIsPurchaseFlow(form.loanPurpose?.value);

    if (!isPurchase && !isChattelPurchase) {
      downPaymentSyncRef.current = null;

      return;
    }

    // LEN-199: a chattel purchase's down payment is a share of its Sales Price, not the Home Value.
    const purchasePrice = isChattelPurchase ? chattelSalesPrice : form.propertyValue;
    const downPaymentValue = form.loanDownPayment;
    const downPaymentPercent = form.loanDownPaymentPercent;

    const prevSync = downPaymentSyncRef.current;
    const hasDownPaymentValueChanged = !prevSync || prevSync.downPaymentValue !== downPaymentValue;
    // On bootstrap (prevSync null) prefer percent driving only when the dollar value hasn't been
    // loaded/entered yet but a percent already has (e.g. a form seeded straight into % editing) --
    // otherwise the persisted/entered dollar value is the source of truth.
    const hasDownPaymentPercentChanged = prevSync
      ? prevSync.downPaymentPercent !== downPaymentPercent
      : _.isNil(getSyncNumberValue(downPaymentValue)) &&
        !_.isNil(getSyncNumberValue(downPaymentPercent));
    const hasPurchasePriceChanged = !!prevSync && prevSync.purchasePrice !== purchasePrice;

    if (
      prevSync &&
      !hasDownPaymentValueChanged &&
      !hasDownPaymentPercentChanged &&
      !hasPurchasePriceChanged
    ) {
      return;
    }

    const nextSync = resolveLeadDownPaymentSync({
      hasPurchasePrice: Number(purchasePrice) > 0,
      hasDownPaymentPercentChanged,
      purchasePrice,
      downPaymentValue: getSyncNumberValue(downPaymentValue),
      downPaymentPercent: getSyncNumberValue(downPaymentPercent),
    });
    const nextDownPaymentValue = getDisplayedNumberValue({
      nextValue: nextSync.downPaymentValue,
      currentValue: downPaymentValue,
    });
    const nextDownPaymentPercent = getDisplayedNumberValue({
      nextValue: nextSync.downPaymentPercent,
      currentValue: downPaymentPercent,
    });

    downPaymentSyncRef.current = {
      purchasePrice,
      downPaymentValue: nextDownPaymentValue,
      downPaymentPercent: nextDownPaymentPercent,
    };

    if (nextDownPaymentValue !== downPaymentValue) {
      setInForm(leadFormFields.loanDownPayment.nameInModel, nextDownPaymentValue, false);
    }

    if (nextDownPaymentPercent !== downPaymentPercent) {
      setInForm(leadFormFields.loanDownPaymentPercent.nameInModel, nextDownPaymentPercent, false);
    }
  }, [
    form.loanPurpose?.value,
    form.propertyValue,
    form.loanDownPayment,
    form.loanDownPaymentPercent,
    isChattelPurchase,
    chattelSalesPrice,
    setInForm,
  ]);

  useEffect(() => {
    onDirtyChange?.(isFormChanged);

    return () => onDirtyChange?.(false);
  }, [isFormChanged, onDirtyChange]);

  const isConvertSubmittingRef = useRef(false);

  const setConvertLoading = useCallback(
    isConvertLoading => {
      setIsLoading(isConvertLoading);
      onLoadingChange?.(isConvertLoading);
    },
    [setIsLoading, onLoadingChange],
  );

  const validateCurrencyFields = useCallback(() => {
    const isRefinanceFlow = checkIsRefinanceFlow(form.loanPurpose?.value);
    const isCashOutAmountInvalid =
      form.loanAmount && Number(form.loanCashOutAmount) >= Number(form.loanAmount);
    // >= : the backend rejects a down payment equal to the purchase price (it implies a $0 loan),
    // so block it on the client with an inline error instead of letting it 400.
    const isDownPaymentInvalid =
      form.propertyValue && Number(form.loanDownPayment) >= Number(form.propertyValue);
    // LEN-199: a chattel purchase's down payment comes off its Sales Price (when one is keyed).
    const isChattelDownPaymentInvalid =
      chattelSalesPrice && Number(form.loanDownPayment) >= Number(chattelSalesPrice);
    const isChattelFlow = checkIsChattelLoanPurpose(form.loanPurpose?.value);

    if (isRefinanceFlow && isCashOutAmountInvalid) {
      setValidationErrors(prevErrors => ({
        ..._.cloneDeep(prevErrors),
        [leadFormFields.loanCashOutAmount.nameInModel]: 'Should be less than Loan Amount',
      }));

      return false;
    }

    if (isChattelFlow) {
      if (isChattelPurchase && isChattelDownPaymentInvalid) {
        setValidationErrors(prevErrors => ({
          ..._.cloneDeep(prevErrors),
          [leadFormFields.loanDownPayment.nameInModel]:
            'Down Payment must be less than the Sales Price',
        }));

        return false;
      }

      return true;
    }

    if (!isRefinanceFlow && isDownPaymentInvalid) {
      setValidationErrors(prevErrors => ({
        ..._.cloneDeep(prevErrors),
        [leadFormFields.loanDownPayment.nameInModel]:
          'Down Payment must be less than the Purchase Price',
      }));

      return false;
    }

    return true;
  }, [
    form.loanPurpose,
    form.loanCashOutAmount,
    form.loanAmount,
    form.loanDownPayment,
    form.propertyValue,
    isChattelPurchase,
    chattelSalesPrice,
    setValidationErrors,
  ]);

  useEffectSafeStrictMode(() => {
    if (!isSelectSubCompanyAllowed || !isCreateNewLead) {
      return;
    }

    setIsLoading(true);

    dispatch(getSubCompaniesAction())
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [isSelectSubCompanyAllowed, isCreateNewLead, setIsLoading, dispatch]);

  const submitEditForm = useCallback(
    async ({ reassignLoanOfficer = false } = {}) => {
      const isFormValid = await validateForm();
      const isCurrenciesValid = validateCurrencyFields();

      if (!isFormValid || !isCurrenciesValid) {
        return;
      }

      setIsLoading(true);

      const successMessage = 'Lead details have been updated';
      const formValues = getActionBody(leadFormFields, form);

      const valuesToSend = {
        ..._.omit(formValues, EXCLUDED_SUBMIT_FIELDS),
        ...getPropertyValues(formValues),
        phoneNumber: normalizeRequestPhoneNumber(formValues.phoneNumber),
        applicationId: leadData.applicationId,
        requestMetadata: { tabId: LoanApplicationTabId.LEAD },
        reassignLoanOfficer,
        // Keep the existing isTest when the Test Lead checkbox is gated out (no createTestLoan claim),
        // so editing an existing test lead can't silently clear the flag.
        isTest: formValues.isTest ?? leadData.isTest,
        ...getAppraisedValue(formValues),
      };

      return dispatch(editLeadByIdAction(valuesToSend))
        .then(() => {
          dispatch(showMessageAction({ message: successMessage, variant: 'success' }));

          dispatch(getLoanApplicationHeaderViewAction({ applicationId: leadData.applicationId }));

          onCancelEditForm();
        })
        .catch(err => {
          const { response } = err;
          const isInvalidUserLicenseStateConflictErrorOccurred = checkIsSpecificConflictError(
            response,
            CONFLICT_TYPE.INVALID_USER_LICENSE_STATE,
          );

          if (isInvalidUserLicenseStateConflictErrorOccurred) {
            return dispatch(
              openConfirmationDialogAction({
                type: ConfirmationModalType.WARNING,
                message: 'Invalid License',
                description: response.data.message,
                onAccept: () => submitEditForm({ ...valuesToSend, reassignLoanOfficer: true }),
                onAcceptButtonName: 'Accept',
              }),
            );
          }

          logger.error(err);
          dispatch(
            showErrorMessageAction({
              message: getDefaultErrorMessage(err),
              err,
            }),
          );
        })
        .finally(() => setIsLoading(false));
    },
    [
      dispatch,
      form,
      leadData.applicationId,
      leadData.isTest,
      onCancelEditForm,
      setIsLoading,
      validateCurrencyFields,
      validateForm,
    ],
  );

  const submitCreateForm = useCallback(async () => {
    if (!(await validateForm()) || !validateCurrencyFields()) {
      return;
    }

    const formValues = getActionBody(leadFormFields, form);

    const valuesToSend = {
      ..._.omit(formValues, EXCLUDED_SUBMIT_FIELDS),
      ...getPropertyValues(formValues),
      phoneNumber: normalizeRequestPhoneNumber(formValues.phoneNumber),
      ...getAppraisedValue(formValues),
    };

    setIsLoading(true);

    return dispatch(createLeadAction(valuesToSend))
      .then(({ payload }) => {
        const { applicationId: generatedApplicationId, loanOfficer } = payload ?? {};

        dispatch(closeDialogAction());
        warnIfLoanOfficerNotAssigned(dispatch, loanOfficer);

        if (generatedApplicationId) {
          LoansPaths.applicationManagement.navigateWith(
            push,
            {
              [LOANS_ROUTING_PARAMS.applicationId]: generatedApplicationId,
              [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.LEAD,
            },
            {
              state: {
                applicationId: generatedApplicationId,
                pathFrom: PipelinesPaths.loanPipelines.path,
              },
            },
          );
        }
      })
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [form, dispatch, push, validateForm, validateCurrencyFields, setIsLoading]);

  const submitConvertForm = useCallback(async () => {
    if (isConvertSubmittingRef.current) {
      return;
    }

    isConvertSubmittingRef.current = true;
    setConvertLoading(true);

    if (!(await validateForm({ noFormChangedValidate: true })) || !validateCurrencyFields()) {
      isConvertSubmittingRef.current = false;
      setConvertLoading(false);

      return;
    }

    const formValues = getActionBody(leadFormFields, form);

    const valuesToSend = {
      ..._.omit(formValues, EXCLUDED_SUBMIT_FIELDS),
      ...getPropertyValues(formValues),
      isFirstTimeHomeBuyer: !!formValues.isFirstTimeHomeBuyer,
      // Keep the existing isTest when the Test Lead checkbox is gated out (no createTestLoan claim).
      isTest: formValues.isTest ?? leadData.isTest,
      ...getAppraisedValue(formValues),
      ...getChattelDetailsValues(formValues),
    };

    onCustomSubmit(valuesToSend).finally(() => {
      isConvertSubmittingRef.current = false;
      setConvertLoading(false);
    });
  }, [
    validateForm,
    validateCurrencyFields,
    form,
    setConvertLoading,
    onCustomSubmit,
    leadData.isTest,
  ]);

  const submitCreateAndConvertForm = useCallback(async () => {
    if (!(await validateForm()) || !validateCurrencyFields()) {
      return;
    }

    const formValues = getActionBody(leadFormFields, form);

    const valuesToSend = {
      ..._.omit(formValues, EXCLUDED_SUBMIT_FIELDS),
      ...getPropertyValues(formValues),
      phoneNumber: normalizeRequestPhoneNumber(formValues.phoneNumber),
      ...getAppraisedValue(formValues),
    };

    setIsLoading(true);

    return dispatch(createLeadAction(valuesToSend))
      .then(({ payload }) => {
        const { applicationId: newApplicationId, loanOfficer } = payload ?? {};

        warnIfLoanOfficerNotAssigned(dispatch, loanOfficer);

        if (!newApplicationId) {
          return;
        }

        const navigateToApplication = tab =>
          LoansPaths.applicationManagement.navigateWith(
            push,
            {
              [LOANS_ROUTING_PARAMS.applicationId]: newApplicationId,
              [LOANS_ROUTING_PARAMS.tab]: tab,
            },
            {
              state: {
                applicationId: newApplicationId,
                pathFrom: PipelinesPaths.loanPipelines.path,
              },
            },
          );

        return dispatch(
          convertLeadToLoanAction(newApplicationId, {
            ...valuesToSend,
            ...getChattelDetailsValues(formValues),
          }),
        )
          .then(() => {
            dispatch(closeDialogAction());
            navigateToApplication(LoanApplicationTabRouteParam.LOAN);
          })
          .catch(err => {
            logger.error(err?.message);
            dispatch(
              showErrorMessageAction({
                message: getConvertAfterCreateErrorMessage(err),
                err,
              }),
            );
            dispatch(closeDialogAction());
            navigateToApplication(LoanApplicationTabRouteParam.LEAD);
          });
      })
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [form, dispatch, push, validateForm, validateCurrencyFields, setIsLoading]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      if (createAsLoan) {
        submitCreateAndConvertForm();

        return;
      }

      if (isConvertLeadToLoan) {
        submitConvertForm();

        return;
      }

      if (isCreateNewLead) {
        submitCreateForm();

        return;
      }

      submitEditForm();
    },
    [
      createAsLoan,
      isConvertLeadToLoan,
      isCreateNewLead,
      submitEditForm,
      submitConvertForm,
      submitCreateForm,
      submitCreateAndConvertForm,
    ],
  );

  const submitButtonLabel = createAsLoan ? 'Create Loan' : 'Create';

  return (
    // The wrapper keeps the progress bar and the form stacked even when the host lays its content
    // out as a flex row (DrawerCDS does), where two full-width siblings would each take half.
    <div className="flex flex-col w-full">
      {(isCreateNewLead || createAsLoan) && (
        <LinearProgress
          className={`w-full ${isLoading ? 'visible' : 'invisible'}`}
          color="primary"
        />
      )}
      <form
        noValidate
        className="flex flex-col items-stretch w-full"
        id={formId}
        onSubmit={onSubmit}
      >
        {!isCreateNewLead && (
          <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT}>
            <Button
              data-testid={LeadTestIds.LEAD_INFORMATION_FORM_SUBMIT_BUTTON}
              disabled={!isFormChanged || isLoading}
              type="submit"
            >
              Done
            </Button>
            <Button
              data-testid={LeadTestIds.LEAD_INFORMATION_FORM_CANCEL_BUTTON}
              type="button"
              onClick={onCancelEditForm}
            >
              Cancel
            </Button>
          </ButtonGroupWrapper>
        )}

        <FormGenerator
          changedFields={changedFields}
          config={formGeneratorConfig}
          form={form}
          formName={FormNames.LEAD}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={3}
          setInForm={setInForm}
          validateField={validateField}
          validationErrors={validationErrors}
        />

        {!hasExternalSubmit && (isCreateNewLead || isConvertLeadToLoan || createAsLoan) && (
          <div className="flex justify-end">
            <Button
              data-testid={LeadTestIds.LEAD_INFORMATION_FORM_SUBMIT_BUTTON}
              disabled={(!isFormChanged && !isConvertLeadToLoan) || isLoading}
              type="submit"
              variant="contained"
            >
              {submitButtonLabel}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

LeadInformationForm.propTypes = {
  ...WithLoadingPropTypes,
  createAsLoan: PropTypes.bool,
  formId: PropTypes.string,
  hasExternalSubmit: PropTypes.bool,
  onLoadingChange: PropTypes.func,
  onDirtyChange: PropTypes.func,
};

export default compose(withLoading, memo)(LeadInformationForm);
